import React, {useEffect, useState} from 'react';
import {useTranslate} from '../../../hooks';
import {
  formatDate,
  useCountry,
  useFlexTransactions,
} from '@resurs-internal/rb-app-api';
import {
  Box,
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import {IconBadge} from '../../../components';
import {FlexOfferDialogRoutes} from './FlexOfferDialog';
import {BaseList} from '@resurs-internal/mui';

export const FlexOfferDialogUpdate: React.FC<{
  navigate: (key: FlexOfferDialogRoutes) => void;
}> = ({navigate}) => {
  const translate = useTranslate();
  const country = useCountry();
  const flex = useFlexTransactions('flex');
  const [enabled, setEnabled] = useState(false);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const handleCheckChange = (id: string) => () => {
    setEnabled(true);
    setChecked((prev) => ({...prev, [id]: !prev[id]}));
  };

  // initialise checked state
  useEffect(() => {
    if (!flex.query.data) return;
    const initial: Record<string, boolean> = {};
    const checked = flex.query.data?.reduce((result, e) => {
      result[e.id] = e?.markedAsDownPayment ?? false;
      return result;
    }, initial);

    setChecked(checked);
  }, [flex.query.data]);

  const onSave = async () => {
    // update all checked
    await flex.updateAll.mutateAsync(
      flex.query.data?.map((e) => ({
        id: e.id,
        postpone: false,
        markAsDownPayment: Boolean(checked[e.id]),
      })),
    );
    navigate('success');
  };

  return (
    <>
      <DialogContent sx={{padding: 0}}>
        <Stack spacing={2} alignItems={'center'} p={3}>
          <IconBadge type={'font'} icon={'f'} size={'xl'} />
          <Typography variant={'h3'} textAlign={'center'}>
            {translate('flex.offer.subtitle')}
          </Typography>
          <Typography variant={'body1'} sx={{width: '100%'}}>
            {translate('flex.offer.description')}
          </Typography>
          <Typography variant={'body1'} sx={{width: '100%'}}>
            {translate('flex.offer.purchases')}
          </Typography>
          <BaseList
            sx={{width: '100%'}}
            items={flex.query.data
              // filter postpone offers
              ?.filter((e) => !e?.postponementStatus?.eligible)
              ?.map((e) => {
                const title =
                  e.merchantName ??
                  e.merchantLocation ??
                  e.transactionTypeText ??
                  '';
                return {
                  id: e.id,
                  title: title,
                  subtitle: formatDate(e.transactionDate),
                  icon: (
                    <Checkbox
                      inputProps={{'aria-label': `${title}`}}
                      checked={Boolean(checked[e.id])}
                      onChange={handleCheckChange(e.id)}
                    />
                  ),
                  endAdornment: (
                    <Typography sx={{fontWeight: 'bold', mr: 1}}>
                      {country.formatAmount(e.amount)}
                    </Typography>
                  ),
                };
              })}
          />
        </Stack>
        <Box
          sx={(theme) => ({
            p: 3,
            background: theme.palette.background.paper,
          })}>
          <Stack spacing={2}>
            <IconBadge type={'font'} icon={'scale-balanced'} inactive={true} />
            <Typography variant={'subtitle2'} component={'p'}>
              {translate('flex.offer.terms.title')}
            </Typography>
            <Typography variant={'caption'}>
              {translate('flex.offer.terms.text')}
            </Typography>
            <Typography variant={'subtitle2'} component={'p'}>
              {translate('flex.offer.undo.title')}
            </Typography>
            <Typography variant={'caption'}>
              {translate('flex.offer.undo.text')}
            </Typography>
          </Stack>
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button variant={'text'} onClick={() => navigate('close')}>
          {translate('common.cancel')}
        </Button>
        <Button variant={'text'} disabled={!enabled} onClick={onSave}>
          {translate('flex.offer.activate')}
        </Button>
      </DialogActions>
    </>
  );
};
